import React from "react";

const S = {
  wrapper: { marginTop:'10px', marginLeft:'0px', border:'3px', padding:'40px', borderStyle:'solid', borderColor:'grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Sample Space, Events, and Axioms of Probability
// ─────────────────────────────────────────────────────────────────
function DiagramSampleSpace() {
  const W = 900, H = 330;

  // Two dice sample space — 36 outcomes, color-code sum values
  const outcomes = [];
  for (let d1 = 1; d1 <= 6; d1++)
    for (let d2 = 1; d2 <= 6; d2++)
      outcomes.push({ d1, d2, sum: d1 + d2 });

  const sumColors = {
    2:'#c0392b', 3:'#c0392b', 4:'#e07b00', 5:'#e07b00', 6:'#27ae60',
    7:'#2E75B6', 8:'#27ae60', 9:'#e07b00', 10:'#e07b00', 11:'#c0392b', 12:'#c0392b'
  };

  const gridOx = 50, gridOy = 50, cellW = 52, cellH = 38;
  const toGx = d1 => gridOx + (d1-1)*cellW + cellW/2;
  const toGy = d2 => gridOy + (d2-1)*cellH + cellH/2;

  // Right panel: axioms
  const panX = 390;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Sample Space, Events, and the Three Axioms of Probability — Two Dice</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Left panel */}
        <rect x={10} y={10} width={370} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={195} y={28} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
          Sample space S: all 36 equally-likely outcomes when rolling two dice
        </text>

        {/* Axis labels */}
        {[1,2,3,4,5,6].map(v => (
          <g key={v}>
            <text x={toGx(v)} y={gridOy-8} textAnchor="middle" fontSize="11" fill="#888">Die1={v}</text>
            <text x={gridOx-10} y={toGy(v)+4} textAnchor="end" fontSize="11" fill="#888">{v}</text>
          </g>
        ))}
        <text x={gridOx-10} y={gridOy-8} textAnchor="end" fontSize="9" fill="#aaa">D2↓</text>

        {/* Grid cells */}
        {outcomes.map(({ d1, d2, sum }) => {
          const cx = toGx(d1), cy = toGy(d2);
          const col = sumColors[sum];
          // Highlight sum=7 with a ring
          const isEvent = sum === 7;
          return (
            <g key={`${d1}-${d2}`}>
              <rect x={cx - cellW/2 + 2} y={cy - cellH/2 + 2} width={cellW-4} height={cellH-4}
                rx={4} fill={`${col}22`} stroke={isEvent ? col : '#eee'} strokeWidth={isEvent ? 2 : 0.8}/>
              <text x={cx} y={cy+4} textAnchor="middle" fontSize="13" fill={col} fontWeight={isEvent?'700':'400'}>
                {sum}
              </text>
            </g>
          );
        })}

        {/* Event label: sum=7 */}
        <text x={195} y={H-32} textAnchor="middle" fontSize="11" fill="#2E75B6" fontWeight="700">
          Event A = {'{sum = 7}'}: bold border → 6 outcomes out of 36 → P(A) = 6/36 = 1/6
        </text>

        {/* Color legend */}
        {[['2-3,11-12','#c0392b'],['4-5,9-10','#e07b00'],['6,8','#27ae60'],['7','#2E75B6']].map(([lbl,col],i) => (
          <g key={i}>
            <rect x={22+i*86} y={H-20} width={14} height={12} rx={2} fill={col} opacity="0.7"/>
            <text x={40+i*86} y={H-11} fontSize="9" fill="#888">sum={lbl}</text>
          </g>
        ))}

        {/* Right panel: axioms */}
        <rect x={panX} y={10} width={W-panX-14} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+(W-panX-14)/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Kolmogorov's Three Axioms
        </text>

        {[
          { y:36,  bg:'#EBF3FB', bc:'#2E75B6', num:'1',
            title:'Non-negativity',
            formula:'P(A) ≥ 0  for all events A',
            note:'Probability is never negative.\nA probability of 0 means impossible.' },
          { y:122, bg:'#f0fff4', bc:'#27ae60', num:'2',
            title:'Normalization',
            formula:'P(S) = 1',
            note:'The probability of the entire\nsample space is exactly 1.' },
          { y:208, bg:'#fff7ee', bc:'#e07b00', num:'3',
            title:'Additivity',
            formula:'P(A∪B) = P(A)+P(B)\nif A∩B = ∅',
            note:'For mutually exclusive events,\nprobabilities add directly.' },
        ].map(({ y, bg, bc, num, title, formula, note }) => (
          <g key={num}>
            <rect x={panX+8} y={y} width={W-panX-30} height={80} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <circle cx={panX+24} cy={y+18} r={13} fill={bc}/>
            <text x={panX+24} y={y+23} textAnchor="middle" fontSize="13" fill="#fff" fontWeight="700">{num}</text>
            <text x={panX+44} y={y+16} fontSize="12" fill={bc} fontWeight="700">{title}</text>
            {formula.split('\n').map((ln,j) => (
              <text key={j} x={panX+44} y={y+32+j*14} fontSize="11" fill="#1F3864"
                fontFamily="'Courier New',monospace" fontWeight="700">{ln}</text>
            ))}
            {note.split('\n').map((ln,j) => (
              <text key={j} x={panX+44} y={y+58+j*14} fontSize="10" fill="#555">{ln}</text>
            ))}
          </g>
        ))}

        {/* Derived result */}
        <rect x={panX+8} y={H-40} width={W-panX-30} height={24} rx={3} fill="#fff0f0" stroke="#c0392b" strokeWidth="1"/>
        <text x={panX+(W-panX-14)/2} y={H-25} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">
          From axioms: P(∅) = 0;  P(Aᶜ) = 1−P(A);  0 ≤ P(A) ≤ 1
        </text>
      </svg>
      <div style={S.dCaption}>
        A sample space S lists every possible outcome of an experiment. An event A is any subset of S. The three Kolmogorov axioms are the entire foundation of probability theory — everything else is derived from them. For two dice there are 36 equally-likely outcomes; the event "sum equals 7" contains 6 of them, giving probability 6/36 = 1/6, the most likely sum.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Addition Rule and Venn Diagrams
// ─────────────────────────────────────────────────────────────────
function DiagramAdditionRule() {
  const W = 900, H = 330;

  // Three Venn scenarios: mutually exclusive, overlapping, subset
  const scenarios = [
    {
      title: 'Mutually Exclusive',
      subtitle: 'A ∩ B = ∅',
      formula: 'P(A∪B) = P(A) + P(B)',
      example: 'Roll a die: A={1,2}, B={5,6}\nP(A)=2/6, P(B)=2/6\nP(A∪B) = 4/6',
      color1: '#2E75B6', color2: '#c0392b', overlap: false,
      fill: '#EBF3FB', bc: '#2E75B6',
    },
    {
      title: 'Overlapping Events',
      subtitle: 'A ∩ B ≠ ∅',
      formula: 'P(A∪B) = P(A)+P(B)−P(A∩B)',
      example: 'Card drawn: A=Heart, B=Face\nP(A)=13/52, P(B)=12/52\nP(A∩B)=3/52 (heart face cards)\nP(A∪B) = 22/52',
      color1: '#2E75B6', color2: '#c0392b', overlap: true,
      fill: '#fff7ee', bc: '#e07b00',
    },
    {
      title: 'Complementary',
      subtitle: 'P(Aᶜ) = 1 − P(A)',
      formula: "P(A) + P(Aᶜ) = 1",
      example: 'P(rain tomorrow) = 0.3\nP(no rain) = 1 − 0.3 = 0.7\nA and Aᶜ are mutually\nexclusive and exhaustive',
      color1: '#27ae60', color2: '#9b59b6', overlap: false,
      fill: '#f0fff4', bc: '#27ae60', complement: true,
    },
  ];

  const panW = (W - 24) / 3 - 5;
  const vCx = (px) => px + panW/2, vCy = 155, rX = 65, rY = 50;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>The Addition Rule — P(A∪B) and Venn Diagram Representations</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        {scenarios.map(({ title, subtitle, formula, example, color1, color2, overlap, fill, bc, complement }, pi) => {
          const px = 10 + pi * (panW + 8);
          const cx = vCx(px);

          // Venn ellipses
          const e1cx = overlap ? cx - 28 : (complement ? cx : cx - 45);
          const e2cx = overlap ? cx + 28 : (complement ? cx : cx + 45);
          const e1cy = vCy, e2cy = vCy;

          return (
            <g key={pi}>
              <rect x={px} y={10} width={panW} height={H-20} rx={5} fill={fill} stroke={bc} strokeWidth="1.8"/>
              <rect x={px+4} y={14} width={panW-8} height={26} rx={3} fill={bc}/>
              <text x={px+panW/2} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">{title}</text>
              <text x={px+panW/2} y={39} textAnchor="middle" fontSize="10" fill="#fff">{subtitle}</text>

              {/* Sample space rectangle */}
              <rect x={px+14} y={vCy-62} width={panW-28} height={124} rx={8}
                fill="#f8f8f8" stroke="#ccd9ea" strokeWidth="1.5"/>
              <text x={px+panW-20} y={vCy-52} fontSize="10" fill="#aaa" fontWeight="700">S</text>

              {/* Venn circles */}
              {complement ? (
                // Full circle for A, outer region for Aᶜ
                <g>
                  <ellipse cx={cx} cy={vCy} rx={rX*0.85} ry={rY*0.85} fill={`${color1}44`} stroke={color1} strokeWidth="2"/>
                  <text x={cx} y={vCy+4} textAnchor="middle" fontSize="13" fill={color1} fontWeight="700">A</text>
                  <text x={px+panW-26} y={vCy+62} fontSize="11" fill={color2} fontWeight="700">Aᶜ</text>
                </g>
              ) : (
                <g>
                  <ellipse cx={e1cx} cy={e1cy} rx={rX} ry={rY}
                    fill={`${color1}33`} stroke={color1} strokeWidth="2"/>
                  <ellipse cx={e2cx} cy={e2cy} rx={rX} ry={rY}
                    fill={`${color2}33`} stroke={color2} strokeWidth="2"/>
                  <text x={e1cx - (overlap?20:10)} y={e1cy+4} textAnchor="middle" fontSize="13" fill={color1} fontWeight="700">A</text>
                  <text x={e2cx + (overlap?20:10)} y={e2cy+4} textAnchor="middle" fontSize="13" fill={color2} fontWeight="700">B</text>
                  {overlap && (
                    <text x={cx} y={vCy+4} textAnchor="middle" fontSize="10" fill="#333" fontWeight="700">A∩B</text>
                  )}
                </g>
              )}

              {/* Formula box */}
              <rect x={px+8} y={vCy+68} width={panW-16} height={24} rx={3} fill="#fff" stroke={bc} strokeWidth="1"/>
              {formula.split('\n').map((ln, j) => (
                <text key={j} x={px+panW/2} y={vCy+82+j*14} textAnchor="middle" fontSize="11"
                  fill={bc} fontFamily="'Courier New',monospace" fontWeight="700">{ln}</text>
              ))}

              {/* Example */}
              <line x1={px+10} y1={vCy+100} x2={px+panW-10} y2={vCy+100} stroke="#ddd" strokeWidth="1"/>
              {example.split('\n').map((ln, j) => (
                <text key={j} x={px+14} y={vCy+114+j*15} fontSize="10" fill="#444"
                  fontFamily="'Courier New',monospace">{ln}</text>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        The addition rule P(A∪B) = P(A) + P(B) − P(A∩B) accounts for the overlap being counted twice when events share outcomes. For mutually exclusive events (A∩B = ∅) the intersection term vanishes. The complement rule P(Aᶜ) = 1 − P(A) is a special case: A and Aᶜ together cover the entire sample space exactly once.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Conditional Probability
// ─────────────────────────────────────────────────────────────────
function DiagramConditionalProbability() {
  const W = 900, H = 340;

  // Two-way table: Disease (D / D') × Test (T+ / T-)
  // P(D) = 0.01, sensitivity P(T+|D) = 0.95, specificity P(T-|D') = 0.90
  const N = 10000;
  const pD = 0.01, sens = 0.95, spec = 0.90;
  const nD = pD * N;          // 100 have disease
  const nDn = N - nD;         // 9900 don't
  const tp = nD * sens;       // 95  true positive
  const fn = nD * (1-sens);   // 5   false negative
  const fp = nDn * (1-spec);  // 990 false positive
  const tn = nDn * spec;      // 8910 true negative

  const tblX = 60, tblY = 50, cw = 110, rh = 44;
  const panX = 530;

  const cells = [
    [tp, fn, nD],
    [fp, tn, nDn],
    [tp+fp, fn+tn, N],
  ];
  const rowLabels = ['Disease D', 'No Disease D\'', 'Total'];
  const colLabels = ['Test +', 'Test −', 'Total'];
  const cellColors = [
    ['#27ae60','#c0392b','#1F3864'],
    ['#c0392b','#27ae60','#1F3864'],
    ['#1F3864','#1F3864','#1F3864'],
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Conditional Probability — P(A|B) = P(A∩B)/P(B)  —  Medical Testing Example</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Left panel: two-way table */}
        <rect x={10} y={10} width={510} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={265} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          10,000 people: P(Disease)=1%, Sensitivity=95%, Specificity=90%
        </text>

        {/* Column headers */}
        {colLabels.map((h, j) => (
          <g key={j}>
            <rect x={tblX+cw*(j+1)} y={tblY} width={cw} height={rh-4} rx={3}
              fill={j===2?'#1F3864':j===0?'#2E75B6':'#9b59b6'}/>
            <text x={tblX+cw*(j+1)+cw/2} y={tblY+rh/2+4} textAnchor="middle"
              fontSize="12" fill="#fff" fontWeight="700">{h}</text>
          </g>
        ))}

        {/* Row headers + data cells */}
        {cells.map((row, i) => (
          <g key={i}>
            <rect x={tblX} y={tblY+(i+1)*rh} width={cw} height={rh-2} rx={3}
              fill={i===2?'#1F3864':'#f8fbff'} stroke="#ccd9ea" strokeWidth="0.5"/>
            <text x={tblX+cw/2} y={tblY+(i+1)*rh+rh/2+4} textAnchor="middle"
              fontSize="11" fill={i===2?'#fff':'#1F3864'} fontWeight="700">{rowLabels[i]}</text>
            {row.map((val, j) => (
              <g key={j}>
                <rect x={tblX+cw*(j+1)} y={tblY+(i+1)*rh} width={cw} height={rh-2}
                  rx={3} fill={i===2||j===2?'#f0f4f8':'#fff'} stroke="#ccd9ea" strokeWidth="0.5"/>
                <text x={tblX+cw*(j+1)+cw/2} y={tblY+(i+1)*rh+rh/2+4}
                  textAnchor="middle" fontSize="15"
                  fill={i<2&&j<2 ? cellColors[i][j] : '#1F3864'} fontWeight="700">{val}</text>
              </g>
            ))}
          </g>
        ))}

        {/* Key conditional probabilities */}
        <text x={50} y={tblY+4*rh+28} fontSize="11" fill="#27ae60" fontWeight="700">
          True Positive Rate (Sensitivity): P(T+|D)  = {tp}/{nD} = {(sens*100).toFixed(0)}%
        </text>
        <text x={50} y={tblY+4*rh+46} fontSize="11" fill="#c0392b" fontWeight="700">
          False Positive Rate: P(T+|D')  = {fp}/{nDn} = {((1-spec)*100).toFixed(0)}%
        </text>
        <text x={50} y={tblY+4*rh+64} fontSize="11" fill="#9b59b6" fontWeight="700">
          Positive Predictive Value: P(D|T+) = {tp}/{tp+fp} ≈ {(tp/(tp+fp)*100).toFixed(1)}% ← surprisingly low!
        </text>

        {/* Right panel */}
        <rect x={panX} y={10} width={W-panX-14} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+(W-panX-14)/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Conditional Probability
        </text>

        {[
          { y:36,  bg:'#EBF3FB', bc:'#2E75B6',
            title:'Definition',
            lines:['P(A|B) = P(A∩B) / P(B)', '"Probability of A given B"', 'Restrict sample space to B,', 'then ask how much is A.'] },
          { y:128, bg:'#f0fff4', bc:'#27ae60',
            title:'Multiplication Rule',
            lines:['P(A∩B) = P(A|B) · P(B)', '        = P(B|A) · P(A)', 'Rearranges the definition.', 'Both forms are equivalent.'] },
          { y:220, bg:'#fff0f0', bc:'#c0392b',
            title:'Base Rate Neglect',
            lines:['P(D|T+) ≠ P(T+|D)', 'Even 95% accurate test has', 'only ~8.8% PPV if P(D)=1%', 'because false positives dominate.'] },
        ].map(({ y, bg, bc, title, lines }) => (
          <g key={y}>
            <rect x={panX+8} y={y} width={W-panX-30} height={86} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <text x={panX+18} y={y+16} fontSize="12" fill={bc} fontWeight="700">{title}</text>
            {lines.map((ln, j) => (
              <text key={j} x={panX+18} y={y+32+j*16} fontSize="11"
                fill={j===0?'#1F3864':'#555'}
                fontFamily={j===0?"'Courier New',monospace":"'Georgia',serif"}
                fontWeight={j===0?'700':'400'}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Conditional probability P(A|B) restricts the universe to outcomes where B has already occurred. The medical testing table illustrates a counterintuitive result: a test with 95% sensitivity and 90% specificity applied to a 1% prevalence population yields a Positive Predictive Value of only ~8.8% — most positive tests are false positives because the disease is rare.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Independence and the Multiplication Rule
// ─────────────────────────────────────────────────────────────────
function DiagramIndependence() {
  const W = 900, H = 340;

  // Probability tree: two coin flips
  // Then show independence check on a second example

  const panW2 = (W - 24) / 2 - 4;

  // Tree: Flip 1 × Flip 2
  const treeData = [
    { f1:'H', f2:'H', p:'1/4', color:'#2E75B6' },
    { f1:'H', f2:'T', p:'1/4', color:'#2E75B6' },
    { f1:'T', f2:'H', p:'1/4', color:'#c0392b' },
    { f1:'T', f2:'T', p:'1/4', color:'#c0392b' },
  ];

  const rootX = 55, rootY = 150;
  const col1X = 200, col2X = 360, leafX = 490;
  const col1Ys = [100, 200];
  const leafYs = [70, 130, 200, 260];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Independence — P(A∩B) = P(A)·P(B) and Probability Trees</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── LEFT: probability tree ── */}
        <rect x={10} y={10} width={510} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={265} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Two fair coin flips — independent events, each P(H)=P(T)=½
        </text>

        {/* Root */}
        <circle cx={rootX} cy={rootY} r={16} fill="#1F3864"/>
        <text x={rootX} y={rootY+5} textAnchor="middle" fontSize="10" fill="#fff" fontWeight="700">Start</text>

        {/* Flip 1 branches */}
        {col1Ys.map((y, i) => (
          <g key={i}>
            <line x1={rootX+16} y1={rootY} x2={col1X-20} y2={y}
              stroke={i===0?'#2E75B6':'#c0392b'} strokeWidth="2"/>
            <text x={(rootX+16+col1X-20)/2} y={y+(rootY-y)/2-8} textAnchor="middle"
              fontSize="11" fill={i===0?'#2E75B6':'#c0392b'} fontWeight="700">½</text>
            <circle cx={col1X} cy={y} r={20} fill={i===0?'#2E75B6':'#c0392b'} stroke="#fff" strokeWidth="2"/>
            <text x={col1X} y={y+5} textAnchor="middle" fontSize="14" fill="#fff" fontWeight="700">
              {i===0?'H':'T'}
            </text>
          </g>
        ))}

        {/* Flip 2 branches */}
        {treeData.map(({ f1, f2, p, color }, i) => {
          const col1Y = i < 2 ? col1Ys[0] : col1Ys[1];
          const f2Color = f2==='H' ? '#2E75B6' : '#c0392b';
          return (
            <g key={i}>
              <line x1={col1X+20} y1={col1Y} x2={col2X-20} y2={leafYs[i]}
                stroke={f2Color} strokeWidth="2"/>
              <text x={(col1X+20+col2X-20)/2} y={(col1Y+leafYs[i])/2-8} textAnchor="middle"
                fontSize="11" fill={f2Color} fontWeight="700">½</text>
              <circle cx={col2X} cy={leafYs[i]} r={18} fill={f2Color} stroke="#fff" strokeWidth="2"/>
              <text x={col2X} y={leafYs[i]+5} textAnchor="middle" fontSize="14" fill="#fff" fontWeight="700">{f2}</text>

              {/* Leaf outcome */}
              <line x1={col2X+18} y1={leafYs[i]} x2={leafX-8} y2={leafYs[i]} stroke="#ddd" strokeWidth="1"/>
              <rect x={leafX} y={leafYs[i]-16} width={100} height={32} rx={4}
                fill={`${color}18`} stroke={color} strokeWidth="1.2"/>
              <text x={leafX+50} y={leafYs[i]-2} textAnchor="middle" fontSize="13" fill={color} fontWeight="700">
                {f1}{f2}
              </text>
              <text x={leafX+50} y={leafYs[i]+12} textAnchor="middle" fontSize="11" fill="#555">
                P = ½×½ = {p}
              </text>
            </g>
          );
        })}

        {/* Labels */}
        <text x={rootX} y={H-20} textAnchor="middle" fontSize="11" fill="#aaa">Start</text>
        <text x={col1X} y={H-20} textAnchor="middle" fontSize="11" fill="#aaa">Flip 1</text>
        <text x={col2X} y={H-20} textAnchor="middle" fontSize="11" fill="#aaa">Flip 2</text>
        <text x={leafX+50} y={H-20} textAnchor="middle" fontSize="11" fill="#aaa">Outcome</text>

        {/* Sum check */}
        <rect x={18} y={H-52} width={480} height={26} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={258} y={H-35} textAnchor="middle" fontSize="11" fill="#2E75B6" fontWeight="700">
          Sum of all leaf probabilities = 1/4+1/4+1/4+1/4 = 1  ✓  (total probability = 1)
        </text>

        {/* ── RIGHT: independence definition panels ── */}
        <rect x={530} y={10} width={W-544} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={530+(W-544)/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Independence and Dependence
        </text>

        {[
          { y:36,  bg:'#EBF3FB', bc:'#2E75B6',
            title:'Independent Events',
            lines:['P(A∩B) = P(A) · P(B)', 'Knowing B gives no information', 'about A. Equivalent condition:', 'P(A|B) = P(A)'] },
          { y:130, bg:'#fff0f0', bc:'#c0392b',
            title:'Dependent Events',
            lines:['P(A∩B) ≠ P(A) · P(B)', 'Knowing B changes the', 'probability of A.', 'P(A|B) ≠ P(A)'] },
          { y:224, bg:'#f0fff4', bc:'#27ae60',
            title:'n Independent Events',
            lines:['P(A₁∩A₂∩…∩Aₙ)', '  = P(A₁)·P(A₂)·…·P(Aₙ)', 'Multiply all probabilities.', 'e.g. 3 heads: (½)³ = 1/8'] },
        ].map(({ y, bg, bc, title, lines }) => (
          <g key={y}>
            <rect x={538} y={y} width={W-556} height={88} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <text x={548} y={y+16} fontSize="12" fill={bc} fontWeight="700">{title}</text>
            {lines.map((ln, j) => (
              <text key={j} x={548} y={y+32+j*16} fontSize="11"
                fill={j===0?'#1F3864':'#555'}
                fontFamily={j===0?"'Courier New',monospace":"'Georgia',serif"}
                fontWeight={j===0?'700':'400'}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Two events are independent when knowledge of one provides no information about the other. For independent events the multiplication rule simplifies to P(A∩B) = P(A)·P(B) — just multiply. The probability tree shows this directly: each path multiplies the branch probabilities, and all four paths sum to 1. Independence is a property to verify, not assume.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Bayes' Theorem
// ─────────────────────────────────────────────────────────────────
function DiagramBayes() {
  const W = 900, H = 360;

  // Bayes example: email spam filter
  // P(Spam)=0.3, P(Word|Spam)=0.6, P(Word|Ham)=0.05
  const pSpam = 0.3, pHam = 0.7;
  const pWgS = 0.6, pWgH = 0.05;
  const pW = pWgS * pSpam + pWgH * pHam;
  const pSgW = (pWgS * pSpam) / pW;

  // Tree to show the calculation
  const treeNodes = [
    { label:'Spam', p: pSpam, color:'#c0392b', children:[
      { label:'Word found', p:pWgS, color:'#c0392b', joint: pWgS*pSpam },
      { label:'No word', p:1-pWgS, color:'#888', joint: (1-pWgS)*pSpam },
    ]},
    { label:'Ham', p: pHam, color:'#27ae60', children:[
      { label:'Word found', p:pWgH, color:'#e07b00', joint: pWgH*pHam },
      { label:'No word', p:1-pWgH, color:'#888', joint: (1-pWgH)*pHam },
    ]},
  ];

  const rootX = 55, rootY = 170;
  const col1X = 200, col2X = 390, leafX = 540;
  const col1Ys = [110, 230];
  const leafYs = [70, 150, 210, 290];

  const panX = 580;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Bayes' Theorem — Updating Probability with New Evidence (Spam Filter Example)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Left: tree */}
        <rect x={10} y={10} width={560} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={285} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          P(Spam)=30%, P(trigger word|Spam)=60%, P(trigger word|Ham)=5%
        </text>

        {/* Root */}
        <circle cx={rootX} cy={rootY} r={16} fill="#1F3864"/>
        <text x={rootX} y={rootY+5} textAnchor="middle" fontSize="9" fill="#fff" fontWeight="700">Email</text>

        {treeNodes.map(({ label, p, color, children }, i) => (
          <g key={i}>
            {/* Root → Level 1 */}
            <line x1={rootX+16} y1={rootY} x2={col1X-22} y2={col1Ys[i]}
              stroke={color} strokeWidth="2.2"/>
            <text x={(rootX+col1X)/2} y={(rootY+col1Ys[i])/2 - 8} textAnchor="middle"
              fontSize="11" fill={color} fontWeight="700">{p}</text>
            <circle cx={col1X} cy={col1Ys[i]} r={22} fill={color} stroke="#fff" strokeWidth="2"/>
            <text x={col1X} y={col1Ys[i]+5} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{label}</text>

            {children.map(({ label: cLabel, p: cp, color: cColor, joint }, j) => {
              const leafIdx = i * 2 + j;
              const isHighlight = cLabel === 'Word found';
              return (
                <g key={j}>
                  {/* Level 1 → Level 2 */}
                  <line x1={col1X+22} y1={col1Ys[i]} x2={col2X-18} y2={leafYs[leafIdx]}
                    stroke={cColor} strokeWidth={isHighlight ? 2.5 : 1.5}
                    strokeDasharray={isHighlight ? '' : '5,3'}/>
                  <text x={(col1X+22+col2X-18)/2} y={(col1Ys[i]+leafYs[leafIdx])/2-8}
                    textAnchor="middle" fontSize="11" fill={cColor} fontWeight="700">{cp}</text>
                  <ellipse cx={col2X} cy={leafYs[leafIdx]} rx={38} ry={18}
                    fill={isHighlight ? `${cColor}22` : '#f8f8f8'}
                    stroke={cColor} strokeWidth={isHighlight ? 2 : 1}/>
                  <text x={col2X} y={leafYs[leafIdx]+5} textAnchor="middle"
                    fontSize="10" fill={cColor} fontWeight={isHighlight?'700':'400'}>{cLabel}</text>

                  {/* Leaf: joint probability */}
                  {isHighlight && (
                    <g>
                      <line x1={col2X+38} y1={leafYs[leafIdx]} x2={leafX-4} y2={leafYs[leafIdx]}
                        stroke={cColor} strokeWidth="1.8"/>
                      <rect x={leafX} y={leafYs[leafIdx]-18} width={130} height={36} rx={4}
                        fill={`${cColor}18`} stroke={cColor} strokeWidth="1.5"/>
                      <text x={leafX+65} y={leafYs[leafIdx]-4} textAnchor="middle"
                        fontSize="10" fill={cColor} fontWeight="700">
                        Joint P = {joint.toFixed(3)}
                      </text>
                      <text x={leafX+65} y={leafYs[leafIdx]+12} textAnchor="middle"
                        fontSize="10" fill={cColor}>
                        {p}×{cp} = {joint.toFixed(3)}
                      </text>
                    </g>
                  )}
                </g>
              );
            })}
          </g>
        ))}

        {/* Total P(Word) */}
        <rect x={leafX-2} y={H-70} width={138} height={28} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.2"/>
        <text x={leafX+67} y={H-53} textAnchor="middle" fontSize="10" fill="#2E75B6" fontWeight="700">
          P(Word) = {pW.toFixed(3)}
        </text>
        <text x={leafX+67} y={H-40} textAnchor="middle" fontSize="9" fill="#555">
          = 0.180 + 0.035
        </text>

        {/* Bayes result */}
        <rect x={18} y={H-40} width={440} height={26} rx={3} fill="#9b59b622" stroke="#9b59b6" strokeWidth="1.2"/>
        <text x={238} y={H-24} textAnchor="middle" fontSize="12" fill="#9b59b6" fontWeight="700"
          fontFamily="'Courier New',monospace">
          P(Spam|Word) = 0.180/0.215 ≈ {(pSgW*100).toFixed(1)}%
        </text>

        {/* Right panel */}
        <rect x={panX} y={10} width={W-panX-14} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+(W-panX-14)/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Bayes' Theorem
        </text>

        {[
          { y:36,  bg:'#EBF3FB', bc:'#2E75B6',
            title:'The Formula',
            lines:["P(A|B) =  P(B|A)·P(A)", "         ──────────────", "              P(B)"] },
          { y:130, bg:'#fff7ee', bc:'#e07b00',
            title:'Expanded denominator',
            lines:['P(B) = P(B|A)·P(A)', '     + P(B|Aᶜ)·P(Aᶜ)', 'Law of Total Probability.', 'Sum over all partitions.'] },
          { y:224, bg:'#f5d0ff', bc:'#9b59b6',
            title:'Interpretation',
            lines:['Prior P(A) + evidence B', '→ Posterior P(A|B)', 'Bayes updates beliefs', 'with new information.'] },
        ].map(({ y, bg, bc, title, lines }) => (
          <g key={y}>
            <rect x={panX+8} y={y} width={W-panX-30} height={88} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <text x={panX+18} y={y+16} fontSize="12" fill={bc} fontWeight="700">{title}</text>
            {lines.map((ln, j) => (
              <text key={j} x={panX+18} y={y+32+j*16} fontSize="11"
                fill={j<=1?bc:'#555'}
                fontFamily={j<=1?"'Courier New',monospace":"'Georgia',serif"}
                fontWeight={j<=1?'700':'400'}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Bayes' Theorem inverts conditional probability: given P(evidence|hypothesis), it computes P(hypothesis|evidence). Starting from a 30% prior probability of spam, observing a trigger word (which appears 60% of the time in spam but only 5% of the time in legitimate email) updates the posterior to ~83.7%. The denominator P(Word) uses the Law of Total Probability, summing across all ways the word can appear.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Law of Total Probability and Summary of Laws
// ─────────────────────────────────────────────────────────────────
function DiagramTotalProbability() {
  const W = 900, H = 340;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Law of Total Probability and Summary of All Probability Laws</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Header */}
        <rect x={14} y={10} width={W-28} height={38} rx={5} fill="#1F3864"/>
        <text x={W/2} y={27} textAnchor="middle" fontSize="14" fill="#fff" fontWeight="700">
          Law of Total Probability: partition S into exhaustive, mutually exclusive events B₁, B₂, …, Bₙ
        </text>
        <text x={W/2} y={42} textAnchor="middle" fontSize="12" fill="#aac8e8"
          fontFamily="'Courier New',monospace">
          P(A) = P(A|B₁)·P(B₁) + P(A|B₂)·P(B₂) + … + P(A|Bₙ)·P(Bₙ)
        </text>

        {/* Partition diagram */}
        {(() => {
          const bx = 14, by = 56, bw = 340, bh = 130;
          const parts = [
            { label:'B₁', w:0.25, color:'#2E75B6' },
            { label:'B₂', w:0.35, color:'#27ae60' },
            { label:'B₃', w:0.40, color:'#9b59b6' },
          ];
          let cumX = bx;
          return (
            <g>
              <rect x={bx} y={by} width={bw} height={bh} rx={6} fill="#f8fbff" stroke="#ccd9ea" strokeWidth="2"/>
              <text x={bx+bw-14} y={by+14} textAnchor="end" fontSize="11" fill="#aaa" fontWeight="700">S</text>
              {parts.map(({ label, w, color }, i) => {
                const pw = bw * w - 2;
                const px = cumX + 1;
                cumX += bw * w;
                return (
                  <g key={i}>
                    <rect x={px} y={by+1} width={pw} height={bh-2} rx={4}
                      fill={`${color}22`} stroke={color} strokeWidth="1.5"/>
                    <text x={px+pw/2} y={by+bh/2-10} textAnchor="middle"
                      fontSize="13" fill={color} fontWeight="700">{label}</text>
                    <text x={px+pw/2} y={by+bh/2+8} textAnchor="middle"
                      fontSize="10" fill={color}>P({label})</text>
                    <text x={px+pw/2} y={by+bh/2+24} textAnchor="middle"
                      fontSize="10" fill="#555">{['25%','35%','40%'][i]}</text>
                  </g>
                );
              })}
              {/* Event A ellipse crossing partitions */}
              <ellipse cx={bx+bw*0.5} cy={by+bh*0.58} rx={bw*0.38} ry={bh*0.28}
                fill="none" stroke="#c0392b" strokeWidth="2.5" strokeDasharray="8,4"/>
              <text x={bx+bw*0.5} y={by+bh*0.92} textAnchor="middle"
                fontSize="11" fill="#c0392b" fontWeight="700">Event A (crosses partitions)</text>

              {/* Arrow down to formula */}
              <text x={bx+bw/2} y={by+bh+22} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">
                P(A) = P(A|B₁)·P(B₁) + P(A|B₂)·P(B₂) + P(A|B₃)·P(B₃)
              </text>
            </g>
          );
        })()}

        {/* Summary of all laws — right side cards */}
        {[
          { x:370, y:56,  w:252, color:'#2E75B6', fill:'#EBF3FB',
            title:'Addition Laws',
            rows:['P(A∪B) = P(A)+P(B)−P(A∩B)', 'ME: P(A∪B) = P(A)+P(B)', 'P(Aᶜ) = 1−P(A)'] },
          { x:630, y:56,  w:256, color:'#c0392b', fill:'#fff0f0',
            title:'Multiplication Laws',
            rows:['P(A∩B) = P(A|B)·P(B)', 'Independent: P(A∩B)=P(A)·P(B)', 'P(A|B) = P(A∩B)/P(B)'] },
          { x:370, y:162, w:252, color:'#27ae60', fill:'#f0fff4',
            title:"Bayes' Theorem",
            rows:['P(A|B) = P(B|A)·P(A) / P(B)', 'Updates prior with evidence', 'Posterior ∝ Likelihood × Prior'] },
          { x:630, y:162, w:256, color:'#9b59b6', fill:'#f5d0ff',
            title:'Total Probability',
            rows:['P(A) = Σᵢ P(A|Bᵢ)·P(Bᵢ)', 'B₁,…,Bₙ partition the space', 'Used as denominator in Bayes'] },
          { x:370, y:268, w:516, color:'#e07b00', fill:'#fff7ee',
            title:'Axioms and Derived Rules',
            rows:['P(A) ≥ 0   |   P(S) = 1   |   P(A∪B)=P(A)+P(B) if A∩B=∅',
                  'P(∅)=0   |   0≤P(A)≤1   |   P(A)≤P(B) if A⊆B'] },
        ].map(({ x, y, w, color, fill, title, rows }) => (
          <g key={title}>
            <rect x={x} y={y} width={w} height={title.includes('Axioms')?58:100} rx={5}
              fill={fill} stroke={color} strokeWidth="1.5"/>
            <rect x={x+4} y={y+4} width={w-8} height={20} rx={3} fill={color}/>
            <text x={x+w/2} y={y+18} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{title}</text>
            {rows.map((r, j) => (
              <text key={j} x={x+10} y={y+36+j*20} fontSize="10" fill={j===0?color:'#555'}
                fontFamily="'Courier New',monospace" fontWeight={j===0?'700':'400'}>{r}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The Law of Total Probability decomposes P(A) by conditioning on an exhaustive partition of the sample space — like summing the probability of an event across all possible "causes." It is the denominator in Bayes' theorem and the bridge between prior and posterior probabilities. The summary cards collect all major probability laws into a single reference.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function LawsOfProbability(props) {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Laws of Probability</h1>

      <p style={S.p}>
        Probability is the mathematical language of uncertainty. It assigns numbers between 0 and 1 to events, where 0 means impossible and 1 means certain. The laws of probability are the rules that govern how these numbers combine, compare, and update — they underpin every statistical inference, every machine learning model, and every risk assessment. Starting from three simple axioms due to Kolmogorov, the entire edifice of probability theory is derived.
      </p>

      <InfoBox
        title="The Foundation: Kolmogorov's Three Axioms"
        items={[
          "Axiom 1 — Non-negativity: P(A) ≥ 0 for every event A",
          "Axiom 2 — Normalization: P(S) = 1 — the full sample space has probability 1",
          "Axiom 3 — Additivity: P(A∪B) = P(A)+P(B) when A and B are mutually exclusive",
          "All other probability rules — complement, addition, multiplication, Bayes — are derived from these three",
          "Classical probability: P(A) = (number of favourable outcomes) / (total equally-likely outcomes)",
        ]}
      />

      <h2 style={S.h2}>Sample Space, Events, and the Axioms</h2>
      <DiagramSampleSpace />
      <Table2 rows={[
        ["Sample space S",     "The set of all possible outcomes of an experiment\nTwo dice: S has 36 elements; a coin: S = {H, T}"],
        ["Event A",            "Any subset of S — a collection of outcomes\nCan be a single outcome, several, or the empty set ∅"],
        ["Classical P",        "P(A) = |A| / |S| when all outcomes are equally likely\nRequires a finite, uniform sample space"],
        ["Empirical P",        "P(A) ≈ (frequency of A in n trials) / n  as n → ∞\nFrequentist interpretation: long-run relative frequency"],
        ["Subjective P",       "P(A) = degree of belief that A will occur\nBayesian interpretation — updated with evidence"],
        ["Derived from axioms","P(∅)=0; P(Aᶜ)=1−P(A); 0≤P(A)≤1; P(A)≤P(B) if A⊆B\nAll follow from the three axioms alone"],
      ]} />

      <h2 style={S.h2}>The Addition Rule and Venn Diagrams</h2>
      <DiagramAdditionRule />
      <Table2 rows={[
        ["General addition rule","P(A∪B) = P(A) + P(B) − P(A∩B)\nSubtract the intersection to avoid double-counting"],
        ["Mutually exclusive",   "A∩B = ∅  →  P(A∪B) = P(A) + P(B)\nEvents that cannot both occur simultaneously"],
        ["Complement rule",      "P(Aᶜ) = 1 − P(A)  where Aᶜ = 'not A'\nA and Aᶜ are exhaustive (union = S) and mutually exclusive"],
        ["Three events",         "P(A∪B∪C) = P(A)+P(B)+P(C)−P(A∩B)−P(A∩C)−P(B∩C)+P(A∩B∩C)\nInclusion-exclusion principle"],
        ["Probability bounds",   "max(P(A)+P(B)−1, 0) ≤ P(A∩B) ≤ min(P(A), P(B))\nFréchet inequalities constrain the intersection"],
        ["Union bound",          "P(A₁∪…∪Aₙ) ≤ P(A₁)+…+P(Aₙ)\nBoole's inequality — equality only when all disjoint"],
      ]} />

      <h2 style={S.h2}>Conditional Probability</h2>
      <DiagramConditionalProbability />
      <Table2 rows={[
        ["Definition",          "P(A|B) = P(A∩B) / P(B),  provided P(B) > 0\nRestrict the sample space to B, ask how much is A"],
        ["Multiplication rule", "P(A∩B) = P(A|B) · P(B) = P(B|A) · P(A)\nRearrangement of the definition"],
        ["Chain rule",          "P(A₁∩A₂∩…∩Aₙ) = P(A₁)·P(A₂|A₁)·P(A₃|A₁∩A₂)·…\nExtends multiplication rule to n events"],
        ["Sensitivity",         "P(T+|Disease) — true positive rate of a diagnostic test\nAlso called recall or hit rate"],
        ["Specificity",         "P(T−|No disease) — true negative rate\nFalse positive rate = 1 − specificity"],
        ["PPV vs sensitivity",  "P(Disease|T+) ≠ P(T+|Disease) — base rate matters enormously\nRare diseases have low PPV even with high-sensitivity tests"],
      ]} />

      <h2 style={S.h2}>Independence and the Multiplication Rule</h2>
      <DiagramIndependence />
      <Table2 rows={[
        ["Independence",        "A and B are independent iff P(A∩B) = P(A)·P(B)\nEquivalently: P(A|B) = P(A)  (B gives no info about A)"],
        ["Mutual independence", "A₁,…,Aₙ mutually independent iff P(∩ subset) = ∏ P(Aᵢ)\nPairwise independence does NOT imply mutual independence"],
        ["Repeated trials",     "n independent identical trials with P(success) = p:\nP(all n succeed) = pⁿ"],
        ["Dependence vs causation","Statistical dependence ≠ causal relationship\nTwo events can be correlated without either causing the other"],
        ["Conditional independence","A and B conditionally independent given C:\nP(A∩B|C) = P(A|C)·P(B|C) — often used in Bayesian networks"],
        ["Sampling with/without replacement","With replacement: draws are independent\nWithout replacement: draws are dependent (hypergeometric)"],
      ]} />

      <h2 style={S.h2}>Bayes' Theorem</h2>
      <DiagramBayes />
      <Table2 rows={[
        ["Bayes' theorem",      "P(A|B) = P(B|A)·P(A) / P(B)\nInverts conditional probability — from P(B|A) to P(A|B)"],
        ["Prior probability",   "P(A) — belief about A before observing evidence B\nStarting point; updated to form the posterior"],
        ["Likelihood",          "P(B|A) — probability of observing evidence B given A is true\nHow well hypothesis A explains the observed evidence"],
        ["Posterior probability","P(A|B) — updated belief after observing B\nProportion to prior × likelihood: P(A|B) ∝ P(B|A)·P(A)"],
        ["Base rate neglect",   "P(A|B) can be very different from P(B|A) when P(A) is small\nCommon error: ignoring the prior probability P(A)"],
        ["Bayesian updating",   "Can apply Bayes repeatedly: today's posterior is tomorrow's prior\nSequential updating with multiple pieces of evidence"],
      ]} />

      <h2 style={S.h2}>Law of Total Probability and Summary</h2>
      <DiagramTotalProbability />
      <Table2 rows={[
        ["Partition of S",       "B₁,B₂,…,Bₙ partition S if: (1) mutually exclusive, (2) ∪Bᵢ = S\nEvery outcome in S belongs to exactly one Bᵢ"],
        ["Total probability",    "P(A) = Σᵢ P(A|Bᵢ)·P(Bᵢ)\nWeighted average of conditional probabilities"],
        ["Denominator in Bayes", "P(B) = P(B|A)·P(A) + P(B|Aᶜ)·P(Aᶜ)  (two-partition case)\nEnsures the posterior sums to 1 across all hypotheses"],
        ["Coherence",            "All probabilities assigned must be consistent with the axioms\nViolating the laws leads to a 'Dutch book' — guaranteed loss"],
        ["Odds form of Bayes",   "Posterior odds = Likelihood ratio × Prior odds\nP(A|B)/P(Aᶜ|B) = [P(B|A)/P(B|Aᶜ)] × [P(A)/P(Aᶜ)]"],
        ["Applications",         "Spam filters, medical diagnosis, DNA evidence, risk assessment\nBayesian inference is used wherever uncertainty must be quantified"],
      ]} />

      <InfoBox
        title="Key Takeaway — Which Law to Use When"
        items={[
          "Combining events? Addition rule: P(A∪B) = P(A)+P(B)−P(A∩B); subtract 0 if mutually exclusive.",
          "P of 'not A'? Complement: P(Aᶜ) = 1−P(A) — often easier than computing P(A) directly.",
          "Both A and B occur? Multiplication: P(A∩B) = P(A|B)·P(B); simplifies to P(A)·P(B) if independent.",
          "Given that B occurred, what is P(A)? Conditional probability: P(A|B) = P(A∩B)/P(B).",
          "Known P(B|A), need P(A|B)? Bayes' theorem: P(A|B) = P(B|A)·P(A)/P(B).",
          "Need P(B) but only know P(B|partition)? Law of Total Probability: P(B) = Σ P(B|Bᵢ)·P(Bᵢ).",
          "Testing independence: check P(A∩B) = P(A)·P(B) — if not equal, events are dependent.",
          "Always check: do all probabilities lie in [0,1] and do exhaustive mutually exclusive events sum to 1?",
        ]}
      />
    </div>
  );
}